import Container from '../../atoms/container/modalButtons';
import Direction from '../../../../shared/types/enums/direction';
import Focus from '../../../../shared/types/enums/focus';
import List from '../../sections/list/list';
import Rail from '@shared/classes/rail';
import RecordButton from '../buttons/recordButton';
import lodash from 'lodash';
import { clearModal } from '../../../../shared/store/slices/modal.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { recordingSelector } from '../../../../shared/store/slices/recordings.slice';
import { reload } from '../../../../shared/store/slices/floatingMenu.slice';
import { removeRecordingRequest } from '../../../../shared/api/recording/recordingsRequest';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { store } from '../../../../shared/store/store';
import {
  popFocus,
  pushFocus,
} from '../../../../shared/store/slices/focus.slice';
import {
  routerSelector,
  updateParams,
  updateStack,
} from '@shared/store/slices/router.slice';
import Event from '@shared/classes/event';
import EpgEvent from '@shared/classes/epgEvent';

export default function RecordingDeleteModalButtons({
  asset,
}: {
  asset: EpgEvent;
}) {
  const dispatch = useAppDispatch();
  const { currentRecording, identifiers } = useAppSelector(recordingSelector);
  const { params } = useAppSelector(routerSelector);

  const { backLiteral, deleteLiteral } = useMemo(() => {
    const backLiteral = 'MODAL.RECORDING.BACK';
    const deleteLiteral = 'MODAL.RECORDING.DELETE'; // TBD: Change literal based on content to be removed (_SEASON or _EPISODE)
    return { backLiteral, deleteLiteral };
  }, []);

  useEffect(() => {
    dispatch(pushFocus(Focus.MODAL_BUTTONS));
    return () => {
      dispatch(popFocus());
    };
  }, []);

  const backButtonAction = useCallback(() => {
    dispatch(clearModal());
  }, []);

  const deleteButtonAction = useCallback(async () => {
    const recording = identifiers.find(
      (item) => item.code === currentRecording?.code
    );
    const { modal } = store.getState();
    if (recording?.deleteId) {
      removeRecordingRequest(
        recording.deleteId,
        (modal?.params as { recordingType: boolean })?.recordingType,
        recording.code
      ).then(() => {
        filterRecording();
        dispatch(reload());
      });
    }
    dispatch(clearModal());
  }, [currentRecording, identifiers]);

  // Logic callbacks

  const filterRecording = useCallback(() => {
    // If recording is from season extended, remove current item
    if (params?.current?.items) {
      let items = lodash.cloneDeep(params.current.items) as Event[];
      // Remove item
      if (items?.length && items.length > 0) {
        items = items.filter((item) => item?.id !== asset?.id);
      }
      dispatch(updateParams({ items }));
      // Remove season recording if no more items found
      if (!items?.length) {
        const currentParams = params.current;
        const stackParams = params.stack;
        const isSeasonRecord =
          params?.current?.isRecord && params?.current?.isSeason;
        if (stackParams?.length && isSeasonRecord) {
          // Generate new current rail
          const currentRail = lodash.cloneDeep(
            stackParams[0]?.currentRail
          ) as Rail;
          currentRail.data = currentRail?.data?.filter(
            (item) => item?.id !== currentParams?.id
          );
          // Generate new rails
          const rails = (lodash.cloneDeep(stackParams[0].rails) as Rail[])
            .map((rail) => (rail.id === currentRail.id ? currentRail : rail))
            .filter((rail) => rail.data.length);
          // Get new indices
          const length = currentRail?.data?.length || 0;
          const horizontalIndex =
            length > 0
              ? ((stackParams[0]?.horizontalIndex as number) || 1) - 1
              : 0;
          const verticalIndex =
            length === 0
              ? Math.min(
                  rails?.length - 1,
                  (stackParams[0]?.verticalIndex as number) || 0
                )
              : stackParams[0]?.verticalIndex;
          // Update stacked params
          dispatch(
            updateStack({
              index: 0,
              params: {
                ...stackParams[0],
                currentRail,
                rails,
                horizontalIndex,
                verticalIndex,
              },
            })
          );
          dispatch(popRoute());
        }
      }
    }
  }, [params]);

  return (
    <Container>
      <List direction={Direction.VERTICAL} focus={Focus.MODAL_BUTTONS}>
        <RecordButton
          index={0}
          literal={deleteLiteral}
          releaseAction={deleteButtonAction}
        />
        <RecordButton
          index={1}
          literal={backLiteral}
          releaseAction={backButtonAction}
        />
      </List>
    </Container>
  );
}
